import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Blue marker for user's restaurants
let BlueRestaurantIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIuNSAwQzUuNiAwIDAgNS42IDAgMTIuNWMwIDkuNCAxMi41IDI4LjUgMTIuNSAyOC41czEyLjUtMTkuMSAxMi41LTI4LjVDMjUgNS42IDE5LjQgMCAxMi41IDB6IiBmaWxsPSIjMzg5NmZmIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMS41Ii8+PGNpcmNsZSBjeD0iMTIuNSIgY3k9IjEyLjUiIHI9IjUiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=',
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Red marker for other users' restaurants
let RedRestaurantIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjUiIGhlaWdodD0iNDEiIHZpZXdCb3g9IjAgMCAyNSA0MSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIuNSAwQzUuNiAwIDAgNS42IDAgMTIuNWMwIDkuNCAxMi41IDI4LjUgMTIuNSAyOC41czEyLjUtMTkuMSAxMi41LTI4LjVDMjUgNS42IDE5LjQgMCAxMi41IDB6IiBmaWxsPSIjZWYzMzRiIiBzdHJva2U9IiNmZmZmZmYiIHN0cm9rZS13aWR0aD0iMS41Ii8+PGNpcmNsZSBjeD0iMTIuNSIgY3k9IjEyLjUiIHI9IjUiIGZpbGw9IiNmZmZmZmYiLz48L3N2Zz4=',
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Custom icon for user location (blue circle)
let UserLocationIcon = L.icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHZpZXdCb3g9IjAgMCAzMCAzMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSIxNSIgY3k9IjE1IiByPSIxMCIgZmlsbD0iIzM4OTZmZiIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIzIi8+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iNCIgZmlsbD0id2hpdGUiLz48L3N2Zz4=',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -15]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Component to handle map actions (must be inside MapContainer)
const LocateButton = ({ onLocate }) => {
  const map = useMap();
  
  return (
    <div 
      onClick={() => onLocate(map)}
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 1000,
        background: 'white',
        border: '2px solid rgba(0,0,0,0.2)',
        borderRadius: '4px',
        padding: '8px',
        cursor: 'pointer',
        boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '34px',
        height: '34px',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f0f0f0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'white';
      }}
      title="Show my location"
    >
      <span style={{ fontSize: '18px' }}>📍</span>
    </div>
  );
};

// Expand/collapse toggle button component
const ExpandButton = ({ onToggle, isExpanded }) => {
  return (
    <div 
      onClick={onToggle}
      style={{
        position: 'absolute',
        top: '10px',
        right: '54px',
        zIndex: 1000,
        background: 'white',
        border: '2px solid rgba(0,0,0,0.2)',
        borderRadius: '4px',
        padding: '8px',
        cursor: 'pointer',
        boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '34px',
        height: '34px',
        transition: 'all 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f0f0f0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'white';
      }}
      title={isExpanded ? "Collapse map" : "Expand map"}
    >
      <span style={{ fontSize: '18px' }}>
        {isExpanded ? '⊟' : '⊞'}
      </span>
    </div>
  );
};

// Component to trigger locate when expanded
const MapExpansionHandler = ({ isExpanded, onLocate }) => {
  const map = useMap();
  const hasLocatedRef = useRef(false);
  
  useEffect(() => {
    if (isExpanded && !hasLocatedRef.current) {
      // Give the DOM time to update, then invalidate size and trigger locate
      setTimeout(() => {
        map.invalidateSize();
        onLocate(map);
        hasLocatedRef.current = true;
      }, 100);
    } else if (!isExpanded) {
      // Reset when collapsed
      hasLocatedRef.current = false;
    }
  }, [isExpanded, map]);
  
  return null;
};

// Component to auto-trigger locate on initial load
const AutoLocateHandler = ({ onLocate, hasAutoLocated, setHasAutoLocated }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!hasAutoLocated && map) {
      // Wait for map to be fully initialized
      const timer = setTimeout(() => {
        if (map && map.setView) {
          console.log('Auto-triggering location on map load');
          onLocate(map);
          setHasAutoLocated(true);
        }
      }, 1500); // Longer delay to ensure map is fully ready
      
      return () => clearTimeout(timer);
    }
  }, [hasAutoLocated, map, onLocate, setHasAutoLocated]);
  
  return null;
};

const RestaurantMap = ({ restaurants, userEmail }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasAutoLocated, setHasAutoLocated] = useState(false);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  
  console.log('RestaurantMap - Total restaurants:', restaurants.length);
  console.log('RestaurantMap - User email:', userEmail);
  
  // Filter restaurants that have valid coordinates
  const restaurantsWithCoords = restaurants.filter(r => 
    r.latitude && r.longitude && 
    r.latitude >= 24.5 && r.latitude <= 49.4 && // Continental USA latitude range
    r.longitude >= -125 && r.longitude <= -66 // Continental USA longitude range
  );
  
  console.log('RestaurantMap - With valid coords:', restaurantsWithCoords.length);
  console.log('RestaurantMap - Sample:', restaurantsWithCoords.slice(0, 2));
  console.log('RestaurantMap - Ownership check:', restaurantsWithCoords.map(r => ({
    name: r.name,
    user_email: r.user_email,
    matches: r.user_email === userEmail
  })));

  // Center on continental USA
  const center = [39.8, -98.5]; // Geographic center of USA
  const zoom = 4;

  // Handle locate button click
  const handleLocate = (map) => {
    if (!map || !map.setView) {
      console.log('Map not ready yet, skipping locate');
      return;
    }
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      console.error('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        setLocationError(null);
        
        // Check if map is still valid before calling setView
        if (map && map.setView) {
          try {
            map.setView([latitude, longitude], 12, {
              animate: true,
              duration: 1
            });
          } catch (err) {
            console.error('Error setting map view:', err);
          }
        }
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred.';
        }
        
        setLocationError(errorMessage);
        console.error('Geolocation error:', errorMessage);
      },
      {
        enableHighAccuracy: false, // Set to false for faster response
        timeout: 10000,
        maximumAge: 60000 // Allow cached location up to 1 minute old
      }
    );
  };

  // Handle expand/collapse toggle
  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
  };

  // Add/remove class from body when expanded to hide page content
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
      // Hide dashboard sections and banners
      const elementsToHide = document.querySelectorAll('.dashboard-view, .dashboard-section, .nearest-banner, .latest-visits-banner, .tabs-container, .header-action-buttons');
      elementsToHide.forEach(element => {
        element.style.visibility = 'hidden';
      });
    } else {
      document.body.style.overflow = '';
      // Show dashboard sections and banners
      const elementsToShow = document.querySelectorAll('.dashboard-view, .dashboard-section, .nearest-banner, .latest-visits-banner, .tabs-container, .header-action-buttons');
      elementsToShow.forEach(element => {
        element.style.visibility = 'visible';
      });
    }
  }, [isExpanded]);

  if (restaurantsWithCoords.length === 0) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center', 
        background: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <p style={{ color: '#64748b', margin: 0 }}>
          No restaurants with location data to display on map.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Expanded overlay backdrop - only blocks pointer events outside map */}
      {isExpanded && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 99998,
            pointerEvents: 'none' // Don't block ANY events by default
          }}
        >
          {/* Clickable overlay that's behind the map */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'auto' // Re-enable for this layer
            }}
            onClick={handleExpandToggle}
          />
        </div>
      )}
      
      <div 
        ref={mapContainerRef}
        style={{ 
          height: isExpanded ? '100vh' : '100%',
          width: isExpanded ? '100vw' : '100%',
          borderRadius: isExpanded ? '0' : '8px',
          overflow: 'hidden',
          position: isExpanded ? 'fixed' : 'relative',
          top: isExpanded ? '0' : 'auto',
          left: isExpanded ? '0' : 'auto',
          transform: isExpanded ? 'none' : 'none',
          zIndex: isExpanded ? 99999 : 'auto',
          backgroundColor: 'white',
          transition: 'none', // Disable transition to prevent re-render issues
          boxShadow: isExpanded ? 'none' : 'none',
          pointerEvents: 'auto' // Ensure map always receives events
        }}
      >
        <MapContainer 
          center={center} 
          zoom={zoom} 
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Auto-locate on initial load */}
          <AutoLocateHandler 
            onLocate={handleLocate} 
            hasAutoLocated={hasAutoLocated}
            setHasAutoLocated={setHasAutoLocated}
          />
          
          {/* Handler to trigger locate when expanded */}
          <MapExpansionHandler isExpanded={isExpanded} onLocate={handleLocate} />
          
          {/* Expand/Collapse Toggle Button */}
          <ExpandButton onToggle={handleExpandToggle} isExpanded={isExpanded} />
          
          {/* Locate Me Button */}
          <LocateButton onLocate={handleLocate} />
          
        {/* Restaurant Markers */}
        {restaurantsWithCoords.map((restaurant) => {
          // Determine if this restaurant belongs to the current user
          const isUserRestaurant = restaurant.user_email === userEmail;
          const markerIcon = isUserRestaurant ? BlueRestaurantIcon : RedRestaurantIcon;
          
          // Debug logging for first restaurant
          if (restaurant === restaurantsWithCoords[0]) {
            console.log('Map Debug - First Restaurant:', {
              name: restaurant.name,
              restaurant_user_email: restaurant.user_email,
              current_userEmail: userEmail,
              isUserRestaurant: isUserRestaurant
            });
          }
          
          return (
            <Marker 
              key={restaurant.id} 
              position={[restaurant.latitude, restaurant.longitude]}
              icon={markerIcon}
            >
              <Popup>
                <div style={{ minWidth: '200px' }}>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 'bold' }}>
                    {restaurant.name}
                  </h3>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#64748b' }}>
                    📍 {restaurant.location}
                  </p>
                  {restaurant.overallRating && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>
                      ⭐ {restaurant.overallRating}/10
                    </p>
                  )}
                  {restaurant.price && (
                    <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>
                      💰 {restaurant.price}
                    </p>
                  )}
                  <p style={{ 
                    margin: '0.5rem 0 0 0', 
                    fontSize: '0.75rem', 
                    color: isUserRestaurant ? '#3896ff' : '#ef334b',
                    fontWeight: '600'
                  }}>
                    {isUserRestaurant ? '🔵 Your Restaurant' : '🔴 Other User\'s Restaurant'}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}
        
        {/* User Location Marker (if available) */}
        {userLocation && (
          <Marker 
            position={[userLocation.latitude, userLocation.longitude]}
            icon={UserLocationIcon}
          >
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '0.875rem' }}>
                  📍 You are here
                </p>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </div>
    </>
  );
};

export default RestaurantMap;
